// src/appinsights.js
import { ApplicationInsights } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";

const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

export const reactPlugin = new ReactPlugin();

let _currentUser = null;

export const setAppInsightsUser = (user) => {
    _currentUser = user;

    if (!appInsights) return;

    if (user?.id) {
        // Mantiene correlación por usuario autenticado sin enviar más PII de la necesaria
        appInsights.setAuthenticatedUserContext(String(user.id), undefined, true);
    } else {
        appInsights.clearAuthenticatedUserContext();
    }
};

export const appInsights = connectionString
    ? new ApplicationInsights({
        config: {
            connectionString,
            extensions: [reactPlugin],

            // El tracking de rutas sigue siendo manual
            enableAutoRouteTracking: false,

            // Mantener correlación distribuida con backend propio
            enableCorsCorrelation: true,

            // Reducir costo y exposición de datos
            enableRequestHeaderTracking: false,
            enableResponseHeaderTracking: false,

            correlationHeaderExcludedDomains: [
                "*.kinde.com",
                "fonts.googleapis.com",
                "fonts.gstatic.com",
                "*.clarity.ms",
                "auth-dev.oncoconnect.cl",
                "auth.oncoconnect.cl",
                // BrowserPrint local (Zebra): no admite request-id/trace headers en CORS preflight
                "127.0.0.1",
                "localhost",
            ],

            // Mantener tracking de fetch/XHR
            disableFetchTracking: false,

            // Menos ruido de depuración
            loggingLevelConsole: 0,
            loggingLevelTelemetry: 1,

            // Desactivar features ruidosas/no esenciales
            enableAjaxErrorStatusText: false,
        },
    })
    : null;

if (appInsights) {
    appInsights.addTelemetryInitializer((item) => {
        item.tags = item.tags ?? {};
        item.data = item.data ?? {};
        item.ext = item.ext ?? {};

        // Nombre visible en Azure
        item.tags["ai.cloud.role"] = "OncoConnect Frontend";
        item.tags["ai.cloud.roleInstance"] = process.env.REACT_APP_ENV ?? "local";

        // Mantener dimensiones útiles y baratas
        item.data["app.version"] = process.env.REACT_APP_VERSION ?? "unknown";
        item.data["app.environment"] = process.env.REACT_APP_ENV ?? "unknown";

        if (_currentUser?.id) {
            // Solo ID para evitar enviar PII innecesaria en todos los eventos
            item.data["user.id"] = String(_currentUser.id);
        }

        const baseType = item.baseType;
        const baseData = item.baseData ?? {};

        // Excluir llamadas ruidosas típicas del frontend
        if (baseType === "RemoteDependencyData") {
            const target = String(baseData.target ?? "");
            const name = String(baseData.name ?? "");
            const data = String(baseData.data ?? "");
            const text = `${target} ${name} ${data}`.toLowerCase();

            const noisyPatterns = [
                "/health",
                "/healthz",
                "/metrics",
                "/live",
                "/ready",
                "fonts.googleapis.com",
                "fonts.gstatic.com",
            ];

            if (noisyPatterns.some((p) => text.includes(p))) {
                return false;
            }

            // Mantener solo dependencias fallidas para reducir costo
            if (baseData.success === true) {
                return false;
            }
        }

        // Reducir tamaño de page views y eventos custom si trajeran data excesiva
        if (baseData.properties && typeof baseData.properties === "object") {
            const cleaned = {};
            for (const [key, value] of Object.entries(baseData.properties)) {
                if (value == null) continue;

                const lowerKey = key.toLowerCase();
                if (
                    lowerKey.includes("password") ||
                    lowerKey.includes("token") ||
                    lowerKey.includes("authorization") ||
                    lowerKey.includes("cookie") ||
                    lowerKey.includes("secret")
                ) {
                    cleaned[key] = "[REDACTED]";
                    continue;
                }

                const strValue =
                    typeof value === "string" ? value : JSON.stringify(value);

                cleaned[key] =
                    strValue.length > 500
                        ? `${strValue.slice(0, 500)}...[truncated]`
                        : strValue;
            }

            baseData.properties = cleaned;
            item.baseData = baseData;
        }

        return true;
    });

    appInsights.loadAppInsights();

    if (process.env.NODE_ENV === "development") {
        console.log("✅ Application Insights inicializado correctamente");
    }
} else {
    console.warn(
        "⚠️ Application Insights NO está inicializado. Verifica REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING"
    );
}
